import type { WritableSignal } from '@angular/core'

import {
  type ChatMessageContent,
  buildAttachmentsMessagePayload,
  localizeString,
  resolveChatSessionId,
  showToast,
  uploadChatFilesAndWait,
} from '@flowx/core-sdk'

/**
 * Orchestrates the attachments send: resolve session → upload all files and wait
 * for the batch SSE result → build the ATTACHMENTS payload → send. On failure,
 * surfaces the localized error as a toast (not in the attachments area, which is
 * reserved for pre-upload errors) and keeps the files for retry (decision 3).
 */
export async function handleAngularAttachments(
  event: { message: string; files: File[] },
  uploadError: WritableSignal<string | null>,
  sendMessage: (message: string, messageContent?: ChatMessageContent) => void,
  onDone: (success: boolean) => void
): Promise<void> {
  uploadError.set(null)
  try {
    const chatSessionId = resolveChatSessionId()
    const files = await uploadChatFilesAndWait(chatSessionId, event.files)
    const payload = buildAttachmentsMessagePayload(event.message, files)
    sendMessage(event.message, payload)
    onDone(true)
  } catch (error) {
    console.error('[AttachmentsUpload] Upload failed:', error)
    const message = localizeString('@@sys_chat_attachments_upload_error')
    // Keep the signal set so the input keeps files/text for retry (effect detection),
    // but surface the message itself only via toast.
    uploadError.set(message)
    showToast({
      messageType: 'error',
      message,
      hideCloseButton: true,
      closeInterval: 3000,
      withTheme: true,
    })
    onDone(false)
  }
}
